/**
 * Backend exchange: trade the entered OTP for an AeroPass JWT. This is the only
 * real deviation from the OwnID flow — the real SDK returns the JWT itself; here
 * the mock BE validates the sandbox OTP and returns it.
 *
 * Contract (POST {apiUrl}, Content-Type: application/json, no auth):
 *   request: { "phoneNumber": "+1…", "code": "0000" }
 *   ALWAYS HTTP 200 — the outcome is in the body:
 *     success: { "jwt": "<aeropass-jwt>", "userId": "<uuid>" }
 *     failure: { "error": { "code": "INVALID_OTP" | …, "message": "…" } }
 */

export interface VerifyParams {
  /** Resolved verify endpoint (env-baked or overridden). */
  apiUrl: string
  /** Digits the user typed. */
  code: string
  /** loginId — must be an existing AeroPass user (sent as `phoneNumber`). */
  phoneNumber: string
}

/** Error codes the BE returns; the widget maps these to copy + behavior. */
export type VerifyErrorCode =
  | 'INVALID_OTP'
  | 'OTP_EXPIRED'
  | 'TOO_MANY_ATTEMPTS'
  | 'ACCOUNT_NOT_FOUND'
  | 'SERVER_ERROR'
  | 'NETWORK'
  | 'BAD_RESPONSE'

export type VerifyResult =
  | { ok: true; jwt: string; userId?: string }
  | { ok: false; code: VerifyErrorCode; message?: string }

const KNOWN_CODES: VerifyErrorCode[] = [
  'INVALID_OTP',
  'OTP_EXPIRED',
  'TOO_MANY_ATTEMPTS',
  'ACCOUNT_NOT_FOUND',
  'SERVER_ERROR',
]

export async function verifyOtp(params: VerifyParams): Promise<VerifyResult> {
  const { apiUrl, code, phoneNumber } = params

  let response: Response
  try {
    response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber, code }),
    })
  } catch {
    return { ok: false, code: 'NETWORK' }
  }

  let data: { jwt?: unknown; userId?: unknown; error?: { code?: unknown; message?: unknown } } = {}
  try {
    data = await response.json()
  } catch {
    // tolerate empty/non-JSON bodies — handled below
  }

  // The BE always replies 200 with the outcome in the body: an `error` object
  // for failures, otherwise the `jwt`. (HTTP status is only a fallback for real
  // infra failures, e.g. a gateway 5xx with no JSON body.)
  const err = data.error
  if (err && typeof err === 'object') {
    return {
      ok: false,
      code: normalizeErrorCode(err.code),
      message: typeof err.message === 'string' ? err.message : undefined,
    }
  }

  const jwt = typeof data.jwt === 'string' ? data.jwt : ''
  if (jwt) {
    return {
      ok: true,
      jwt,
      userId: typeof data.userId === 'string' ? data.userId : undefined,
    }
  }

  // Neither error nor jwt — malformed body, or an infra-level non-200.
  return { ok: false, code: response.ok ? 'BAD_RESPONSE' : 'SERVER_ERROR' }
}

/** Trust a known `error.code`; anything unknown/missing falls back to INVALID_OTP. */
function normalizeErrorCode(code: unknown): VerifyErrorCode {
  if (typeof code === 'string' && (KNOWN_CODES as string[]).includes(code)) {
    return code as VerifyErrorCode
  }
  return 'INVALID_OTP'
}
